// SOVRANOVA — device-bound encryption for at-rest offline data.
// Uses a NON-EXTRACTABLE AES-GCM key the browser holds in IndexedDB. This
// removes plaintext-at-rest: forensic disk inspection, browser-profile
// backup/sync leakage, casual storage snooping, and most XSS "dump storage"
// payloads see only ciphertext, and the key cannot be exported.
// It is NOT end-to-end / zero-knowledge: on an UNLOCKED device the running app
// can still decrypt (true zero-knowledge would need a user passphrase, which
// the Supabase login model doesn't have).
//
// All functions are async and degrade to null on any failure (missing
// crypto.subtle, non-secure context, IndexedDB unavailable) so callers treat
// it as a cache-miss rather than throwing — storage must never break the app.

const DB_NAME = 'sovranova-keystore';
const STORE = 'keys';
const KEY_ID = 'aes-gcm-v1';
const DB_VERSION = 1;

function openKeyDB() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) { reject(e); }
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const r = tx.objectStore(STORE).get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

function idbPut(db, key, val) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(val, key);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

// Get-or-create the device key. Cached in-memory after first resolve.
// IndexedDB can structured-clone-store a CryptoKey (incl. non-extractable).
let keyPromise = null;
function getKey() {
  if (keyPromise) return keyPromise;
  keyPromise = (async () => {
    if (!globalThis.crypto?.subtle || !globalThis.indexedDB) return null;
    const db = await openKeyDB();
    try {
      const existing = await idbGet(db, KEY_ID);
      if (existing) return existing;
      const key = await crypto.subtle.generateKey(
        { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
      );
      await idbPut(db, KEY_ID, key);
      return key;
    } finally { try { db.close(); } catch (_) {} }
  })().catch(() => null);
  return keyPromise;
}

function toB64(buf) {
  let s = ''; const a = new Uint8Array(buf);
  for (let i = 0; i < a.length; i++) s += String.fromCharCode(a[i]);
  return btoa(s);
}
function fromB64(b64) {
  const s = atob(b64); const a = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
  return a;
}

// Encrypt any JSON-serialisable value -> "g1:<ivB64>.<ctB64>" string, or null.
export async function encryptToString(obj) {
  try {
    const key = await getKey();
    if (!key) return null;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(JSON.stringify(obj));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
    return 'g1:' + toB64(iv) + '.' + toB64(ct);
  } catch (_) { return null; }
}

// Decrypt a "g1:" string back to its value, or null on any failure.
export async function decryptFromString(str) {
  try {
    if (typeof str !== 'string' || !str.startsWith('g1:')) return null;
    const key = await getKey();
    if (!key) return null;
    const body = str.slice(3);
    const dot = body.indexOf('.');
    if (dot < 0) return null;
    const iv = fromB64(body.slice(0, dot));
    const ct = fromB64(body.slice(dot + 1));
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
    return JSON.parse(new TextDecoder().decode(pt));
  } catch (_) { return null; }
}

export function isEncrypted(str) {
  return typeof str === 'string' && str.startsWith('g1:');
}
